import { Link, useLocation } from "react-router-dom";
import { SiteFooter } from "../components/SiteFooter";
import { SEO } from "../components/SEO";
import { LEGAL_DOCS, type LegalDocId } from "../data/legalData";
import { LEGAL } from "../lib/brand";
import gcLogo from "../assets/gclogo2026.png";

const FOOTER_LINKS = [
  { label: "The studio", to: "/" },
  { label: "Olga's profile", to: "/olga" },
  { label: "Contact", to: "/#contact" },
];

export function LegalPage({ docId }: { docId: LegalDocId }) {
  const doc = LEGAL_DOCS[docId];
  const { pathname } = useLocation();

  return (
    <div className="relative w-full min-h-screen bg-[#e7f1f1] overflow-x-hidden flex flex-col">
      <SEO
        variant="agency"
        title={`${doc.title} — Geekcow Creative Studio`}
        description={doc.summary}
        url={`https://www.geekcow.com${pathname}`}
      />

      {/* Slim header — legal pages do not need the full nav */}
      <header className="w-full px-4 md:px-[116px] py-5 border-b border-[#141414]/10">
        <div className="max-w-[900px] mx-auto flex items-center justify-between gap-4">
          <Link to="/" aria-label="Geekcow — home">
            <img src={gcLogo} alt="Geekcow Creative Studio" className="h-[40px] w-auto" />
          </Link>
          <nav className="flex gap-4 text-[13px] font-medium text-[#003e5b]" aria-label="Legal documents">
            {Object.values(LEGAL_DOCS).map((other) => (
              <Link
                key={other.path}
                to={other.path}
                className={`underline-offset-4 hover:underline ${
                  other.path === pathname ? "font-bold text-[#141414] underline" : ""
                }`}
              >
                {other.navLabel}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="flex-1 w-full px-4 md:px-[116px] py-12 md:py-20">
        <article className="max-w-[760px] mx-auto flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <p className="font-mono text-[11px] tracking-[2px] uppercase text-[#666]">Legal</p>
            <h1 className="font-black text-[36px] md:text-[56px] leading-[1.05] tracking-tight text-[#141414]">
              {doc.title}
            </h1>
            <p className="text-[16px] md:text-[18px] leading-[1.6] text-[#666]">{doc.summary}</p>
            <p className="text-[13px] text-[#7d8a8a]">Last updated: {doc.lastUpdated}</p>
          </div>

          <div className="flex flex-col gap-8">
            {doc.sections.map((section) => (
              <section key={section.heading} className="flex flex-col gap-3">
                <h2 className="font-bold text-[20px] md:text-[24px] leading-tight text-[#141414]">
                  {section.heading}
                </h2>
                {section.body.map((paragraph, i) => (
                  <p key={i} className="text-[15px] md:text-[16px] leading-[1.7] text-[#4a5555]">
                    {paragraph}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="flex flex-col gap-2 pl-5 list-disc marker:text-[#01e12d]">
                    {section.bullets.map((bullet, i) => (
                      <li key={i} className="text-[15px] md:text-[16px] leading-[1.7] text-[#4a5555]">
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="mt-4 rounded-[20px] border border-[#141414]/15 bg-white p-6">
            <h2 className="font-bold text-[18px] text-[#141414]">Who you are dealing with</h2>
            <p className="mt-2 text-[15px] leading-[1.7] text-[#4a5555]">
              {LEGAL.entity}, a {LEGAL.form}.
              <br />
              KvK (Dutch Chamber of Commerce) number: {LEGAL.kvk}.
              <br />
              Contact:{" "}
              <a
                href="mailto:hello@geekcow.com"
                className="font-medium text-[#003e5b] underline underline-offset-4"
              >
                hello@geekcow.com
              </a>
            </p>
          </div>
        </article>
      </main>

      <SiteFooter
        face="agency"
        blurb="Secure, GDPR-ready and accessible websites for European SMEs and NGOs. Audited, not templated."
        navLinks={FOOTER_LINKS}
        onSectionClick={() => {}}
      />
    </div>
  );
}
